/**
 * 디자인 원본 스크립트(바닐라 DOM 조작)를 React 페이지 안에서 안전하게 돌리기 위한 도우미
 *
 * - addEventListener / setTimeout / setInterval 을 추적해서 페이지를 벗어날 때 한 번에 정리
 * - 각 스크립트는 `init(root, scope)` 형태로 만들고, 반환 없이 scope.onDispose 로 정리 등록
 */
export interface Scope {
  addEventListener: typeof window.addEventListener;
  setTimeout: typeof window.setTimeout;
  setInterval: typeof window.setInterval;
  clearTimeout: typeof window.clearTimeout;
  clearInterval: typeof window.clearInterval;
  onDispose(fn: () => void): void;
  dispose(): void;
}

export function createScope(): Scope {
  const ac = new AbortController();
  const timers = new Set<number>();
  const cleanups: Array<() => void> = [];

  const scope: Scope = {
    addEventListener: ((type: string, fn: EventListenerOrEventListenerObject, opts?: boolean | AddEventListenerOptions) => {
      const o = typeof opts === 'object' ? opts : { capture: !!opts };
      window.addEventListener(type, fn, { ...o, signal: ac.signal });
    }) as typeof window.addEventListener,
    setTimeout: ((fn: TimerHandler, ms?: number, ...args: unknown[]) => {
      const id = window.setTimeout(fn, ms, ...args);
      timers.add(id);
      return id;
    }) as typeof window.setTimeout,
    setInterval: ((fn: TimerHandler, ms?: number, ...args: unknown[]) => {
      const id = window.setInterval(fn, ms, ...args);
      timers.add(id);
      return id;
    }) as typeof window.setInterval,
    clearTimeout: (id) => window.clearTimeout(id),
    clearInterval: (id) => window.clearInterval(id),
    onDispose: (fn) => {
      cleanups.push(fn);
    },
    dispose: () => {
      ac.abort();
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
      timers.clear();
      cleanups.splice(0).reverse().forEach((fn) => fn());
    },
  };
  return scope;
}
